import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./AgencyEmployeeList.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import Loading from "../../../loading/Loading";
import CloseIcon from "../../../../components/icons/CloseIcon";
import AgencyEmployeeTable from "./AgencyEmployeeTable";
import { AgencyEmployeeApi } from "../../../../utils/api/AgencyEmployeeApi";
import ListTopButtonsAndSearch from "./ListTopButtonsAndSearch";
import AgencyEmployeeFilter from "../filter/AgencyEmployeeFilter";
import { CommonEnums } from "../../../../enums/common.enums";
import { RootState } from "../../../../redux/store";
import { commonAction } from "../../../../redux/features/commonSlice";

export interface IAgencyEmployeeData {
  _id: string;
  agency_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  mobile_number?: string;
  mobile_country_code?: string;
  designation?: string;
  status?: string;
  profile_photo?: any;
  createdAt?: string;
  updatedAt?: string;
}

let searchTimer: any;

interface stateType {
  isLoading: boolean;
  totalPage: number;
  pageNumber: number;
  totalDocs: number;
  limit: number;
  listData: IAgencyEmployeeData[];
  search: string;
}

const AgencyEmployeeList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const resetFilter = useSelector(
    (state: RootState) => state?.common?.resetFilter,
  );

  const [state, setState] = useState<stateType>({
    isLoading: true,
    totalPage: 1,
    pageNumber: 1,
    totalDocs: 0,
    limit: 20,
    listData: [],
    search: "",
  });

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "00-00-00") return "";
    const parts = dateString.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateString;
  };

  const formatLabel = (value: string) => {
    if (!value) return "";
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const status = searchParams.get(CommonEnums.queryParams?.status) || "";
  const start_date =
    searchParams.get(CommonEnums.queryParams?.start_date) || "";
  const end_date = searchParams.get(CommonEnums.queryParams?.end_date) || "";

  const filterValues = {
    status,
    startDate: start_date || "00-00-00",
    endDate: end_date || "00-00-00",
  };

  const getListDataApi = async (search: string, page: number) => {
    setState({
      ...state,
      isLoading: true,
    });

    let res = await AgencyEmployeeApi.list({
      page,
      limit: state.limit,
      search,
      status: status ? status : "",
      startDate: start_date ? start_date : "",
      endDate: end_date ? end_date : "",
    });

    if (res?.success && res?.data?.success) {
      setState({
        ...state,
        isLoading: false,
        listData: res?.data?.data?.docs || [],
        totalPage: res?.data?.data?.totalPages || 1,
        totalDocs: res?.data?.data?.totalDocs || 0,
        limit: res?.data?.data?.limit || state.limit,
        search,
      });
    } else {
      setState({
        ...state,
        isLoading: false,
        listData: [],
      });
    }
  };

  const onClickPreview = () => {
    setState({
      ...state,
      pageNumber: state.pageNumber - 1,
    });
  };

  const onClickNext = () => {
    setState({
      ...state,
      pageNumber: state.pageNumber + 1,
    });
  };

  const onChangeSearch = (e: any) => {
    let search = e?.target?.value;
    setState({
      ...state,
      search,
      pageNumber: 1,
    });
  };

  const handleRemoveFilter = (updates: any) => {
    const params = new URLSearchParams(searchParams);

    if ("status" in updates) {
      params.set(CommonEnums.queryParams.status, updates.status || "");
    }
    if ("startDate" in updates) {
      params.set(
        CommonEnums.queryParams.start_date,
        updates.startDate === "00-00-00" ? "" : updates.startDate,
      );
    }
    if ("endDate" in updates) {
      params.set(
        CommonEnums.queryParams.end_date,
        updates.endDate === "00-00-00" ? "" : updates.endDate,
      );
    }

    navigate(`/agency-employee?${params.toString()}`);
    dispatch(commonAction.resetFilter());
  };

  const handleClearFilters = () => {
    navigate("/agency-employee");
    dispatch(commonAction.resetFilter());
  };

  useEffect(() => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      getListDataApi(state?.search, state?.pageNumber);
    }, 500);
  }, [state?.search, state?.pageNumber]);

  useEffect(() => {
    getListDataApi(state?.search, state?.pageNumber);
  }, [resetFilter, searchParams]);

  const isFilterActive =
    filterValues.status ||
    (filterValues.startDate !== "00-00-00" &&
      filterValues.endDate !== "00-00-00");

  const startRange = (state.pageNumber - 1) * state.limit + 1;
  const endRange = Math.min(state.pageNumber * state.limit, state.totalDocs);

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="agency-employee-list-container">
          <div className="list-heading">
            <p>Agency employees</p>
            <span className="sub-heading">View and manage your employees</span>
          </div>

          <ListTopButtonsAndSearch
            add_btn_name="Add"
            add_btn_link="/agency-employee/create"
            reset_btn_link="/agency-employee"
            placeholder="Search by name or email"
            onChangeSearch={onChangeSearch}
            type={CommonEnums.pageTypes?.agency_employee?.key}
          />

          {isFilterActive && (
            <div className="applied-filters-container">
              <p className="applied-label font-inter-400">Applied filters</p>

              <div className="chips-wrapper">
                {filterValues.status && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Status: {formatLabel(filterValues.status)}
                    </span>
                    <div
                      className="close-btn"
                      onClick={() => handleRemoveFilter({ status: "" })}
                    >
                      <CloseIcon />
                    </div>
                  </div>
                )}

                {filterValues.startDate !== "00-00-00" && (
                  <div className="filter-chip font-inter-400">
                    <span className="value">
                      Date created: {formatDate(filterValues.startDate)} -{" "}
                      {formatDate(filterValues.endDate)}
                    </span>
                    <div
                      className="close-btn"
                      onClick={() =>
                        handleRemoveFilter({
                          startDate: "00-00-00",
                          endDate: "00-00-00",
                        })
                      }
                    >
                      <CloseIcon />
                    </div>
                  </div>
                )}

                <div
                  className="clear-all-btn font-inter-400"
                  onClick={handleClearFilters}
                >
                  Clear all filters
                </div>
              </div>
            </div>
          )}

          {state.isLoading ? (
            <div className="data-not-found">
              <Loading />
            </div>
          ) : state.listData.length === 0 ? (
            <div className="data-not-found">
              <p>List is Empty!</p>
            </div>
          ) : (
            <AgencyEmployeeTable
              listData={state.listData}
              onRefresh={() => getListDataApi(state.search, state.pageNumber)}
            />
          )}

          {!state.isLoading && state.listData.length > 0 && (
            <div className="pagination-wrapper">
              <div className="viewing-text">
                Viewing {state.totalDocs > 0 ? startRange : 0}-{endRange} of{" "}
                {state.totalDocs} results
              </div>

              <div className="custom-pagination">
                <button
                  className="page-btn"
                  disabled={state.pageNumber === 1}
                  onClick={onClickPreview}
                >
                  ‹
                </button>

                <button
                  className="page-btn"
                  disabled={state.pageNumber === state.totalPage}
                  onClick={onClickNext}
                >
                  ›
                </button>
              </div>
            </div>
          )}

          <AgencyEmployeeFilter />
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AgencyEmployeeList;
